import React, { useEffect, useState } from "react";
import {
  deleteIcon,
  editIconInBox,
} from "../../../assets";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import useDeviceType from "../../../custom-hooks/DeviceType";
import DischargeFormPreviewModel from "../../../utils/discharge-form-preview-model/DischargeFormPreviewModel";
import Input from "../../../component-lib/Atoms/Input/Input";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import { TYPE_OF_SEVERITY } from "../../../component-lib/Organisms/create-visit-dialog/constants";
import EventEHRPatientProfileDetailCard from "../../../component-lib/Organisms/patient-profile-detail-card/EventEHRPatientProfileDetailCard";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import DispositionDialog from "./dialogs/DispositionDialog";
import EventEHRCareVisitFlowButtons from "../../../component-lib/Molecules/care-visit-flow-buttons/EventEHRCareVisitFlowButtons";
import EventEHREditChiefComplaintDialog from "./dialogs/edit-chief-complaint-dialog/EventEHREditChiefComplaintDialog";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getVisitNoteById,
  patchVisitNoteData,
  postVisitNoteData,
} from "../patientSaga";
import {
  componentKey,
  setCloseChiefComplaintDialog,
  setCloseDispositionDialog,
  setDataToDischargeForm,
  setUpdateVisitNoteDataOnceDraftNote,
} from "../patientSlice";
import {
  getPatientDetailsDataFunction,
  decideStatusOnButtonClick,
  setVisitNoteInfoDisable,
} from "../constants";
import { eventEHRCustomToastMessage } from "../../../component-lib/Atoms/custom-toast-message/EventEHRCustomToastMessage";
import DiagnosisCodeSelect from "./DiagnosisCodeSelect";
import IntervalNotesSection from "./IntervalNotesSection";

const MentalHealthCareVisitFlow = () => {
  const {
    visitNoteCareFlowData,
    closeChiefComplaintDialog,
    closeDispositionDialog,
    updateVisitNoteDataOnceDraftNote,
    notesData,
  } = useSelector((state: any) => state[componentKey]);

  let index = visitNoteCareFlowData?.visit_note?.findIndex(
    (obj: any) => obj.id === notesData.id
  );
  // Set index to last  element if not found

  if (index === -1) {
    index = visitNoteCareFlowData?.visit_note?.length - 1;
  }

  const [searchParams] = useSearchParams();
  const isAddNewNote = searchParams.get("isAddNewNote"); // Replace 'yourParam' with your parameter name
  const newNoteStatus = searchParams.get("newNoteStatus"); // Replace 'yourParam' with your parameter name
  const [addNewNote, setAddNewNote] = useState(isAddNewNote || false);

  const isPatientTransfer = isAddNewNote
    ? false
    : setVisitNoteInfoDisable(visitNoteCareFlowData?.status);

  // Interval notes are always available in this flow (same as Physician care).
  // The footer "File Interval Note" button commits the typed note.
  const [intervalNoteText, setIntervalNoteText] = useState("");
  const [showNewField, setShowNewField] = useState(false);
  const currentVisitNote = visitNoteCareFlowData?.visit_note?.[index];
  const intervalNotes = (visitNoteCareFlowData?.visit_note || [])
    .flatMap((vn: any) => vn?.intervalNotes || [])
    .sort(
      (a: any, b: any) =>
        new Date(a?.createdAt).getTime() - new Date(b?.createdAt).getTime()
    );

  const handleFileIntervalNote = () => {
    if (!intervalNoteText || intervalNoteText.trim().length === 0) {
      eventEHRCustomToastMessage({
        title: `Please enter an interval note.`,
        status: "error",
      });
      return;
    }
    // Persist any SOAP documentation entered so far (mirrors the draft-save
    // fields) so it isn't lost when filing the interval note.
    const soapDraftPayload = {
      historyOfPresent: formFieldData?.presentIllnessHistory || undefined,
      objectiveSymptoms: formFieldData?.symptoms || undefined,
      severity: formFieldData?.severityLevel || undefined,
      plan: formFieldData?.plan || undefined,
      assessmentDescription: formFieldData?.assessmentDescription || undefined,
      ...(selectedDiagnosis?.length
        ? {
            assessment: selectedDiagnosis.map((data: any) => ({
              code: data?.label,
              desc: data?.description,
            })),
          }
        : {}),
    };
    const apiPayload = {
      status: "InInterval",
      ...soapDraftPayload,
      intervalNotes: [{ note: intervalNoteText.trim() }],
    };
    if (currentVisitNote?.id) {
      dispatch(
        patchVisitNoteData({
          apiPayload,
          comesFrom: "intervalNote",
          visitNoteId: currentVisitNote?.id,
          patientId,
          navigate,
        })
      );
    } else {
      dispatch(
        postVisitNoteData({
          apiPayload: { ...apiPayload, patientId, visitId: visitNoteId },
          comesFrom: "intervalNote",
          patientId,
          navigate,
        })
      );
    }
    setIntervalNoteText("");
    setShowNewField(false);
  };

  const patientDetailsData = visitNoteCareFlowData;

  const { patientId } = useParams();
  const navigate = useNavigate();
  const { visitNoteId } = useParams();
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const [openDischargeForm, setOpenDischargeForm] = useState(false);
  const [openDispositionDialog, setOpenDispositionDialog] = useState(false);
  const [openChiefComplaintDialog, setOpenChiefComplaintDialog] =
    useState(false);

  const [selectedDiagnosis, setSelectedDiagnosis] = useState<any>([]);
  const [selectedDispositionKey, setSelectedDispositionKey] = useState("");
  const [dischargeNote, setDischargeNote] = useState("");

  const [formFieldData, setFormFieldData] = useState({
    presentIllnessHistory: "",
    care: "woundCare",
    careValue: "",
    description: "",
    assessmentDescription: "",
    plan: "",
    symptoms: "",
    severityLevel: "low",
  });

  // PATIENT DETAILS DATA
  const patientDetails = getPatientDetailsDataFunction(patientDetailsData);

  const ICDCodeColumns = [
    {
      field: "srNo",
      label: <div>Sr No</div>,
      renderLogic: (row: any, index: any) => {
        return <p className="text-neutral-90 font-normal">{index + 1}</p>;
      },
    },
    {
      field: "code",
      label: <div>Code</div>,
      renderLogic: (row: any) => {
        return <p className="text-primary-main font-normal">{row?.label}</p>;
      },
    },
    {
      field: "details",
      label: <div className="w-[70vw]">Details</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.description}</p>
        );
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => {
        return (
          <>
            <div className="flex items-center">
              {!isPatientTransfer && (
                <img
                  alt="delete-icon"
                  className="cursor-pointer"
                  src={deleteIcon}
                  width={"18px"}
                  height={"19.5px"}
                  onClick={() => {
                    // Delete Particular Item
                    setSelectedDiagnosis((prevItems: any) =>
                      prevItems.filter((item: any) => item.id !== row?.id)
                    );
                  }}
                />
              )}
            </div>
          </>
        );
      },
    },
  ];
  // UPDATE DRAFT DATA IF EXISTS
  useEffect(() => {
    if (
      !addNewNote ||
      newNoteStatus === "InDisposition" ||
      newNoteStatus === "Visit Created" ||
      newNoteStatus === "Draft" ||
      newNoteStatus === "InInterval"
    ) {
      // Update selectedDiagnosis based on provided data or fallback to default
      setSelectedDiagnosis(
        selectedDiagnosis?.length
          ? selectedDiagnosis.map((data: any, index: number) => ({
              id: data?.code,
              index,
              label: data?.label,
              value: data?.value,
              description: data?.description,
            }))
          : visitNoteCareFlowData?.visit_note?.[index]?.assessment?.map(
              (data: any, index: number) => ({
                id: data?.code,
                index,
                label: data?.code,
                value: data?.code,
                description: data?.desc,
              })
            ) || []
      );

      // Update formFieldData while preserving existing fields
      setFormFieldData((prevData) => ({
        ...prevData,
        presentIllnessHistory:
          prevData?.presentIllnessHistory ||
          visitNoteCareFlowData?.visit_note?.[index]?.historyOfPresent ||
          "",
        symptoms:
          prevData?.symptoms ||
          visitNoteCareFlowData?.visit_note?.[index]?.objectiveSymptoms ||
          "",
        severityLevel:
          prevData?.severityLevel ||
          visitNoteCareFlowData?.visit_note?.[index]?.severity ||
          "",
        assessmentDescription:
          prevData?.assessmentDescription ||
          visitNoteCareFlowData?.visit_note?.[index]?.assessmentDescription ||
          "",
        plan:
          prevData?.plan ||
          visitNoteCareFlowData?.visit_note?.[index]?.plan ||
          "",
      }));
    }
  }, [visitNoteCareFlowData]);

  useEffect(() => {
    dispatch(
      setDataToDischargeForm({
        ...visitNoteCareFlowData,
        assessment: selectedDiagnosis,
      })
    );
  }, [visitNoteCareFlowData, selectedDiagnosis]);

  // CLOSE DISPOSITION DIALOG
  useEffect(() => {
    if (closeDispositionDialog) {
      dispatch(setCloseDispositionDialog(false));
      setOpenDispositionDialog(false);
      setOpenDischargeForm(true);
    }
  }, [closeDispositionDialog]);

  // ONCE CHIEF COMPLAINT DIALOG CLOSE REFRESH DATA
  useEffect(() => {
    if (closeChiefComplaintDialog) {
      setOpenChiefComplaintDialog(false);
      dispatch(setCloseChiefComplaintDialog(false));
    }
    if (updateVisitNoteDataOnceDraftNote) {
      dispatch(setUpdateVisitNoteDataOnceDraftNote(false));
    }
    const payload = {
      id: visitNoteId,
      comesFrom: "mentalHealthCare",
    };
    dispatch(getVisitNoteById(payload));
  }, [
    closeChiefComplaintDialog,
    updateVisitNoteDataOnceDraftNote,
    closeDispositionDialog,
  ]);



  // DRAFT VISIT NOTE DATA
  const handleButtonClick = (status: any, extraParams?: string) => {
    // CHECK IF ICD 10 CODE SELECTED OR NOT IF NOT THEN RETURN FROM  FUNCTION
    if (extraParams !== "comesFromOrder") {
      if (!selectedDiagnosis || selectedDiagnosis?.length === 0) {
        eventEHRCustomToastMessage({
          title: `Please select at least one ICD-10 code.`,
          status: "error",
        });
        return;
      }
    }
    // Mental Health visits do not require triage/vital signs — no triage gate.
    // Open the disposition dialog only after the save succeeds (passed to the
    // saga as onSuccess). Undefined for Draft saves so they never open it.
    const onSuccess =
      status !== "Draft"
        ? () => setOpenDispositionDialog(true)
        : extraParams === "draftSave"
          ? () => navigate(`/patient/${patientId}?tab=visits`)
          : undefined;

    // Carry an un-filed interval note into this save (Proceed to Discharge) so
    // it isn't lost. Not on order-driven saves.
    const pendingInterval = intervalNoteText?.trim();
    const intervalNotePayload =
      extraParams !== "comesFromOrder" && pendingInterval
        ? { intervalNotes: [{ note: pendingInterval }] }
        : {};
    try {
      if (visitNoteCareFlowData?.visit_note.length === 0) {
        const payload = {
          apiPayload: {
            historyOfPresent: formFieldData?.presentIllnessHistory || undefined,
            objectiveSymptoms: formFieldData?.symptoms || undefined,
            severity: formFieldData?.severityLevel || undefined,
            plan: formFieldData?.plan || undefined,
            assessmentDescription:
              formFieldData?.assessmentDescription || undefined,
            patientId: patientId,
            visitId: visitNoteId,
            assessment: selectedDiagnosis?.map((data: any) => ({
              code: data?.label,
              desc: data?.description,
            })),
            status: status,
            ...intervalNotePayload,
          },
          comesFrom: extraParams,
          navigate,
          onSuccess,
        };
        dispatch(postVisitNoteData(payload));
      } else if (
        addNewNote &&
        !(
          newNoteStatus === "InDisposition" ||
          newNoteStatus === "Visit Created" ||
          newNoteStatus === "Draft"
        )
      ) {
        setAddNewNote(false);
        const payload = {
          apiPayload: {
            historyOfPresent: formFieldData?.presentIllnessHistory || undefined,
            objectiveSymptoms: formFieldData?.symptoms || undefined,
            severity: formFieldData?.severityLevel || undefined,
            plan: formFieldData?.plan || undefined,
            assessmentDescription:
              formFieldData?.assessmentDescription || undefined,
            patientId: patientId,
            visitId: visitNoteId,
            assessment: selectedDiagnosis?.map((data: any) => ({
              code: data?.label,
              desc: data?.description,
            })),
            status: status,
            ...intervalNotePayload,
          },
          comesFrom: extraParams,
          navigate,
          onSuccess,
        };
        dispatch(postVisitNoteData(payload));
      } else {
        const payload = {
          apiPayload: {
            historyOfPresent: formFieldData?.presentIllnessHistory || undefined,
            objectiveSymptoms: formFieldData?.symptoms || undefined,
            severity: formFieldData?.severityLevel || undefined,
            plan: formFieldData?.plan || undefined,
            assessmentDescription:
              formFieldData?.assessmentDescription || undefined,
            assessment: selectedDiagnosis?.map((data: any) => ({
              code: data?.label,
              desc: data?.description,
            })),
            status: status,
            ...intervalNotePayload,
          },
          comesFrom: extraParams,
          visitNoteId: visitNoteCareFlowData?.visit_note?.[index]?.id,
          navigate,
          onSuccess,
        };
        dispatch(patchVisitNoteData(payload));
      }
      if (pendingInterval && extraParams !== "comesFromOrder") {
        setIntervalNoteText("");
      }
      dispatch(
        setDataToDischargeForm({
          ...visitNoteCareFlowData,
          assessment: selectedDiagnosis,
        })
      );
      // The disposition dialog is now opened by `onSuccess` from the saga, only
      // after the visit-note save succeeds (not here, regardless of result).
    } catch (error) {
      console.log("error:", error);
    }
  };

  return (
    <WrapperCard
      customClasses="m-3 p-3 pl-4 pr-4 flex-1 min-h-0 overflow-auto"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col justify-between gap-3 h-full">
        <div className="flex flex-col justify-between gap-3 overflow-hidden">
          {/* HEADING */}
          <Heading title="Visit Note" customStyle={{ color: "#1D1D1D" }} />
          {/* MAIN CONTENT */}
          <div className="pr-2 overflow-auto">
            {/* PROFILE INFO CARD */}
            <div className="flex background-color-F1F1F1 border-radius-8px py-4 px-6">
              <EventEHRPatientProfileDetailCard
                isOpenHealthHistoryPresent={true}
                patientDetails={patientDetails}
                patientId={patientId}
              />
            </div>
            {/* SUBJECTIVE */}
            <div className="flex flex-col pt-3">
              <Heading
                title={TEXT_CONSTANTS?.SUBJECTIVE}
                customStyle={{
                  fontWeight: 500,
                  color: "#1D1D1D",
                  fontSize: "16px",
                  marginBottom: "8px",
                }}
              />
              <div className="flex justify-between items-center mb-2">
                <Heading
                  title="Chief Complaint"
                  customStyle={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#565656",
                  }}
                />
                {!isPatientTransfer && (
                  <img
                    alt="edit-icon"
                    src={editIconInBox}
                    width={"36px"}
                    height={"36px"}
                    className="cursor-pointer"
                    onClick={() => {
                      setOpenChiefComplaintDialog(true);
                    }}
                  />
                )}
              </div>
              <div>
                <div
                  className="font-weight-500 border-radius-8px color-979797"
                  style={{
                    padding: "14px 16px",
                    border: "1px solid #D2D2D2",
                    background: "#F8F8F8",
                  }}
                >
                  {visitNoteCareFlowData?.chiefComplaints}
                </div>
                <div className="mt-2">
                  <Heading
                    title="History of Present Illness"
                    customStyle={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#565656",
                      marginBottom: "8px",
                    }}
                  />
                  <Input
                    rows={3}
                    disabled={isPatientTransfer}
                    size="small"
                    textFieldLabel="Enter History of Present Illness here"
                    multiline={true}
                    maxWords={250}
                    name="presentIllnessHistory"
                    value={formFieldData.presentIllnessHistory}
                    onChangeCb={(e: any) => {
                      setFormFieldData((prevData: any) => ({
                        ...prevData,
                        presentIllnessHistory: e?.target?.value,
                      }));
                    }}
                  />
                </div>
              </div>
              {/* OBJECTIVE */}
              <div className="mt-6">
                <Heading
                  title="Objective"
                  customStyle={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#1D1D1D",
                    marginBottom: "8px",
                  }}
                />
                {/* SYMPTOMS */}
                <div>
                  <Heading
                    title="Symptoms"
                    customStyle={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#565656",
                      marginBottom: "8px",
                    }}
                  />
                  <Input
                    // rows={3}
                    size="small"
                    disabled={isPatientTransfer}
                    textFieldLabel="Enter Symptoms here"
                    // multiline={true}
                    // maxWords={250}
                    name="symptoms"
                    value={formFieldData.symptoms}
                    onChangeCb={(e: any) => {
                      setFormFieldData((prevData: any) => ({
                        ...prevData,
                        symptoms: e?.target?.value,
                      }));
                    }}
                  />
                </div>
                {/* SEVERITY */}
                <div>
                  <div className="mt-4">
                    <Heading
                      title="Severity"
                      customStyle={{
                        fontWeight: 500,
                        fontSize: "16px",
                        color: "#393939",
                        marginBottom: "12px",
                      }}
                    />
                    <div style={{ maxWidth: "350px" }}>
                      <SelectDropdown
                        disabled={isPatientTransfer}
                        label=""
                        name="severityLevel"
                        placeholderValue="Select Severity"
                        displayEmpty={true}
                        value={formFieldData?.severityLevel}
                        options={TYPE_OF_SEVERITY}
                        onChangeCb={(e) =>
                          setFormFieldData((prevData: any) => ({
                            ...prevData,
                            severityLevel: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* TABLE */}
              <div className="mt-6">
                <Heading
                  title="Assessment"
                  customStyle={{ fontSize: "16px" }}
                />

                {/* Assessment Description */}
                <div className="mt-2">
                  <Input
                    disabled={isPatientTransfer}
                    rows={3}
                    maxWords={250}
                    placeholder={"Enter Assessment Description"}
                    multiline
                    size="small"
                    name="assessmentDescription"
                    value={formFieldData.assessmentDescription}
                    onChangeCb={(e: any) => {
                      setFormFieldData((prevData: any) => ({
                        ...prevData,
                        assessmentDescription: e?.target?.value,
                      }));
                    }}
                  />
                </div>

                <div className="mt-6">
                  <DiagnosisCodeSelect
                    setSelectedDiagnosis={setSelectedDiagnosis}
                    isPatientTransfer={isPatientTransfer}
                  />
                </div>
              </div>

              <div className="overflow-auto mb-2 mt-6 max-h-[350px]">
                <Table
                  columns={ICDCodeColumns}
                  rows={selectedDiagnosis}
                  noDataFoundMessage={"No DSM Code entered"}
                />
              </div>

              {/* Plan */}
              <div className="mt-6">
                <Heading
                  title="Plan"
                  customStyle={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#565656",
                    marginBottom: "8px",
                  }}
                />
                <Input
                  disabled={isPatientTransfer}
                  rows={3}
                  placeholder="Enter Plan"
                  multiline
                  name="plan"
                  size="small"
                  maxWords={250}
                  value={formFieldData.plan}
                  onChangeCb={(e: any) => {
                    setFormFieldData((prevData: any) => ({
                      ...prevData,
                      plan: e?.target?.value,
                    }));
                  }}
                />
              </div>
              {/* INTERVAL NOTES (moved to bottom — used for ongoing reassessments) */}
              <div className="pt-3">
                <IntervalNotesSection
                  intervalNotes={intervalNotes}
                  isReadOnly={isPatientTransfer}
                  noteText={intervalNoteText}
                  onNoteTextChange={setIntervalNoteText}
                  showNewField={showNewField}
                  onAddMore={() => setShowNewField(true)}
                />
              </div>
            </div>
          </div>
        </div>
        {/* FOOTER BUTTONS */}
        {!isPatientTransfer && (
          <EventEHRCareVisitFlowButtons
            isTransferButtonPresent={false}
            isFileIntervalNotePresent={true}
            handleFileIntervalNoteButtonClickEvent={handleFileIntervalNote}
            handleCancelButtonClickEvent={() => {
              navigate(`/patient/${patientId}?tab=visits`);
            }}
            handleSaveAsDraftButtonClickEvent={() => {
              handleButtonClick("Draft", "draftSave");
            }}
            handleProceedToDischargeButtonClickEvent={() => {
              const status = decideStatusOnButtonClick(visitNoteCareFlowData);
              handleButtonClick(status);
              // setOpenDischargeForm(true);
            }}
          />
        )}
      </div>
      {openDischargeForm && (
        <DischargeFormPreviewModel
          patientId={patientId}
          visitNoteId={visitNoteId}
          selectedDispositionKey={selectedDispositionKey}
          dischargeNote={dischargeNote}
          open={openDischargeForm}
          comesFrom={selectedDispositionKey !== "discharged" ? "soapNote" : ""}
          handleCloseModel={() => {
            setSelectedDispositionKey("");
            setOpenDischargeForm(false);
          }}
        />
      )}
      {openDispositionDialog && (
        <DispositionDialog
          open={openDispositionDialog}
          patientId={patientId}
          visitNoteId={visitNoteCareFlowData?.visit_note?.[index]?.id}
          handleCloseDialog={() => {
            setOpenDispositionDialog(false);
          }}
          handleDischargeClick={(type, data) => {
            if (type === "discharged") {
              setSelectedDispositionKey("discharged");
              setDischargeNote(data);

              setOpenDischargeForm(true);
            }
          }}
        />
      )}
      {/* EDIT CHIEF COMPLAINT DIALOG */}
      {openChiefComplaintDialog && (
        <EventEHREditChiefComplaintDialog
          defaultSelectedData={visitNoteCareFlowData?.chiefComplaints}
          dialogHeading="Edit Chief Complaint"
          isOpen={openChiefComplaintDialog}
          handleClose={() => {
            setOpenChiefComplaintDialog(false);
          }}
        />
      )}
    </WrapperCard>
  );
};

export default MentalHealthCareVisitFlow;
